import { useCallback } from 'react';
import { EntitiesService } from '@/services/entities.service';

/**
 * Backend settings structure (snake_case from API)
 */
interface BackendEntitySettings {
  prepayment_asset_account_codes?: string[];
  excluded_pnl_account_codes?: string[];
  default_expense_account_code?: string;
  default_amortization_months?: number;
  amortization_materiality_threshold?: number;
  amortization_apply_50_percent_rule?: boolean;
  auto_sync_enabled?: boolean;
  sync_frequency?: 'hourly' | 'daily' | 'weekly' | 'manual';
  sync_spend_money?: boolean;
  transaction_sync_start_date?: string;
  sync_invoices?: boolean;
  sync_bills?: boolean;
  sync_payments?: boolean;
  sync_bank_transactions?: boolean;
  sync_journal_entries?: boolean;
  auto_post_proposed_journals?: boolean;
  enable_ai_scanning?: boolean;
  universal_ocr_enabled?: boolean;
  email_forwarding?: {
    enabled?: boolean;
    forward_to_email?: string | null;
    max_forwards_per_hour?: number;
  };
  base_currency_code?: string;
  initial_sync_completed?: boolean;
  last_full_sync_date?: string;
}

/**
 * Shared hook for entity settings functionality
 * Used by both Dashboard and EntityManagement pages to avoid code duplication
 */
export function useEntitySettings() {

  // Fetch entity settings with proper data transformation
  const handleFetchSettings = useCallback(async (entityId: string) => {
    try {
      // Always bypass local cache when loading settings so we see the latest values
      const entity = await EntitiesService.getEntity(entityId, true);
      const settings = (entity.settings || {}) as BackendEntitySettings;

      // Derive base currency: preference order → settings, entity.base_currency, country_code mapping, fallback
      const countryToCurrency: Record<string, string> = {
        'GB': 'GBP',
        'US': 'USD',
        'CA': 'CAD',
        'AU': 'AUD',
        'NZ': 'NZD',
        'IE': 'EUR',
        'DE': 'EUR',
        'FR': 'EUR',
        // add more as needed
      };

      // Settings may default to 'USD' when the field is absent; treat that as undefined
      const settingsCurrency = settings.base_currency_code;

      const derivedBaseCurrency =
        (settingsCurrency && settingsCurrency !== 'USD' ? settingsCurrency : undefined) ||
        (entity as any).base_currency ||
        countryToCurrency[(entity as any).country_code || ''] ||
        'GBP';

      return {
        entityId: entity.entity_id,
        entityName: entity.entity_name,
        prepaymentAssetAccountCodes: settings.prepayment_asset_account_codes || [],
        defaultExpenseAccountCode: settings.default_expense_account_code || null,
        defaultAmortizationMonths: settings.default_amortization_months || 12,
        amortizationMaterialityThreshold: settings.amortization_materiality_threshold || 1000,
        amortizationApply50PercentRule: settings.amortization_apply_50_percent_rule ?? true,
        autoSyncEnabled: settings.auto_sync_enabled ?? true,
        syncFrequency: settings.sync_frequency || 'daily',
        syncSpendMoney: settings.sync_spend_money ?? true,
        excludedPnlAccountCodes: settings.excluded_pnl_account_codes || [],
        transactionSyncStartDate: settings.transaction_sync_start_date || '',
        syncInvoices: settings.sync_invoices ?? true,
        syncBills: settings.sync_bills ?? true,
        syncPayments: settings.sync_payments ?? true,
        syncBankTransactions: settings.sync_bank_transactions ?? true,
        syncJournalEntries: settings.sync_journal_entries ?? true,
        autoPostProposedJournals: settings.auto_post_proposed_journals ?? false,
        // Backend field is enable_llm_prepayment_detection, but API returns it as enable_ai_scanning
        aiScanningEnabled: settings.enable_ai_scanning ?? false,
        universalOcrEnabled: settings.universal_ocr_enabled ?? true,
        emailForwarding: {
          enabled: settings.email_forwarding?.enabled ?? false,
          maxForwardsPerHour: settings.email_forwarding?.max_forwards_per_hour ?? 50
        },
        baseCurrencyCode: derivedBaseCurrency,
      };
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  }, []);

  // Fetch chart of accounts
  const handleFetchChartOfAccounts = useCallback(async (entityId: string) => {
    try {
      const chartOfAccounts = await EntitiesService.getChartOfAccounts(entityId);
      return chartOfAccounts;
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
      throw error;
    }
  }, []);

  // Save entity settings with proper data transformation
  const handleSaveSettings = useCallback(async (entityId: string, settings: any, onDataRefresh?: () => Promise<void>) => {
    try {
      // Convert camelCase to snake_case for backend API
      const backendSettings: any = {
        prepayment_asset_account_codes: settings.prepaymentAssetAccountCodes,
        default_expense_account_code: settings.defaultExpenseAccountCode,
        default_amortization_months: settings.defaultAmortizationMonths,
        amortization_materiality_threshold: settings.amortizationMaterialityThreshold,
        amortization_apply_50_percent_rule: settings.amortizationApply50PercentRule,
        auto_sync_enabled: settings.autoSyncEnabled,
        sync_frequency: settings.syncFrequency,
        sync_spend_money: settings.syncSpendMoney,
        excluded_pnl_account_codes: settings.excludedPnlAccountCodes,
        sync_invoices: settings.syncInvoices,
        sync_bills: settings.syncBills,
        sync_payments: settings.syncPayments,
        sync_bank_transactions: settings.syncBankTransactions,
        sync_journal_entries: settings.syncJournalEntries,
        auto_post_proposed_journals: settings.autoPostProposedJournals,
        // Frontend uses aiScanningEnabled, backend maps enable_ai_scanning to enable_llm_prepayment_detection
        enable_ai_scanning: settings.aiScanningEnabled,
        universal_ocr_enabled: settings.universalOcrEnabled,
        email_forwarding: settings.emailForwarding
          ? {
              enabled: settings.emailForwarding.enabled,
              max_forwards_per_hour: settings.emailForwarding.maxForwardsPerHour ?? 50
            }
          : undefined
        // base_currency_code removed - should not be editable, comes from accounting system
      };

      // Only include transaction_sync_start_date if it's not empty
      if (settings.transactionSyncStartDate && settings.transactionSyncStartDate.trim() !== '') {
        backendSettings.transaction_sync_start_date = settings.transactionSyncStartDate;
      }

      await EntitiesService.updateEntitySettings(entityId, backendSettings);
      
      // Refresh data if callback provided
      if (onDataRefresh) {
        await onDataRefresh();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      throw error;
    }
  }, []);

  return {
    handleFetchSettings,
    handleFetchChartOfAccounts,
    handleSaveSettings
  };
}
